import { PDFDocument } from 'pdf-lib';

import { metaRaw } from './dataTable';

export type FileKind =
  | 'pdf'
  | 'image'
  | 'excel'
  | 'csv'
  | 'word'
  | 'powerpoint'
  | 'text'
  | 'zip'
  | 'audio'
  | 'video'
  | 'generic';

export function getFileKindFromMime(mime?: string): FileKind {
  if (!mime) return 'generic';
  const m = mime.toLowerCase();

  // Single-purpose first
  if (m === 'application/pdf') return 'pdf';
  if (
    m === 'application/vnd.ms-excel' ||
    m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  )
    return 'excel';
  if (m === 'text/csv' || m === 'application/csv') return 'csv';
  if (
    m === 'application/msword' ||
    m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  )
    return 'word';
  if (
    m === 'application/vnd.ms-powerpoint' ||
    m === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  )
    return 'powerpoint';

  // Families
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('text/')) return 'text';
  if (m.startsWith('audio/')) return 'audio';
  if (m.startsWith('video/')) return 'video';

  // Archives
  if (
    m === 'application/zip' ||
    m === 'application/x-zip-compressed' ||
    m === 'application/x-7z-compressed' ||
    m === 'application/x-rar-compressed' ||
    m === 'application/x-tar'
  )
    return 'zip';

  return 'generic';
}

const fileTypeMap: Record<string, string[]> = {
  pdf: ['application/pdf'],
  jpg: ['image/jpeg', '.jpg'],
  jpeg: ['image/jpeg', '.jpeg'],
  png: ['image/png', '.png'],
  gif: ['image/gif', '.gif'],
  bmp: ['image/bmp', '.bmp'],
  svg: ['image/svg+xml', '.svg'],
  webp: ['image/webp', '.webp'],

  xls: ['application/vnd.ms-excel', '.xls'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', '.xlsx'],
  csv: ['text/csv', '.csv'],

  doc: ['application/msword', '.doc'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', '.docx'],

  ppt: ['application/vnd.ms-powerpoint', '.ppt'],
  pptx: ['application/vnd.openxmlformats-officedocument.presentationml.presentation', '.pptx'],

  odt: ['application/vnd.oasis.opendocument.text', '.odt'],
  ods: ['application/vnd.oasis.opendocument.spreadsheet', '.ods'],
};

export type SupportedExtension = keyof typeof fileTypeMap;

export function getAllowedFileTypes(extensions: SupportedExtension[]): string {
  const types: string[] = [];

  extensions.forEach((ext) => {
    const lower = ext.toLowerCase();
    if (fileTypeMap[lower]) {
      types.push(...fileTypeMap[lower]);
    }
  });

  return types.join(', ');
}

export function getMime(doc: any): string | undefined {
  const mime = metaRaw<string>(doc?.mimeType);
  return typeof mime === 'string' ? mime : undefined;
}

export function getFileName(doc: any): string | undefined {
  return (
    metaRaw<string>(doc?.fileName) ??
    metaRaw<string>(doc?.name) ??
    metaRaw<string>(doc?.originalName) ??
    undefined
  );
}

export function hasExt(doc: any, ext: string): boolean {
  const fn = getFileName(doc)?.toLowerCase();
  return !!fn && fn.endsWith(ext.toLowerCase());
}

/**
 * Convert bytes to megabytes (MB or MiB).
 *
 * @param bytes - Input size in bytes.
 * @param options.decimals - Number of decimal places (default: 2).
 * @param options.binary - If true, use MiB (1024^2). If false, use MB (1000^2). Default: false.
 * @returns Megabytes as a number (rounded to the given decimals).
 */
export function toMegabyte(
  bytes: number,
  options: { decimals?: number; binary?: boolean } = {},
): number {
  const { decimals = 2, binary = false } = options;

  // Guard invalid values
  if (!Number.isFinite(bytes) || bytes < 0) return 0;

  const divisor = binary ? 1024 ** 2 : 1000 ** 2;
  const value = bytes / divisor;

  // Round to fixed decimals but return Number
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * Convenience formatter that returns a string with unit.
 * Example: "12.34 MB" or "11.77 MiB"
 */
export function formatMegabytes(
  bytes: number,
  options: { decimals?: number; binary?: boolean } = {},
): string {
  const { decimals = 2, binary = false } = options;
  const num = toMegabyte(bytes, { decimals, binary });
  const unit = binary ? 'MiB' : 'MB';
  return `${num.toFixed(decimals)} ${unit}`;
}

// -- Type guards -----------------------------------------------------------

export function isImage(doc: any): boolean {
  const mime = getMime(doc);
  return !!mime?.startsWith('image/');
}

export function isPdf(doc: any): boolean {
  const mime = getMime(doc);
  return mime === 'application/pdf' || hasExt(doc, '.pdf');
}

export function isExcel(doc: any): boolean {
  const mime = getMime(doc);
  return (
    mime === 'application/vnd.ms-excel' ||
    mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    hasExt(doc, '.xls') ||
    hasExt(doc, '.xlsx')
  );
}

export function isCsv(doc: any): boolean {
  const mime = getMime(doc);
  return mime === 'text/csv' || mime === 'application/csv' || hasExt(doc, '.csv');
}

export function isDoc(doc: any): boolean {
  const mime = getMime(doc);
  return (
    mime === 'application/msword' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    hasExt(doc, '.doc') ||
    hasExt(doc, '.docx')
  );
}

export type PreviewKind = 'image' | 'pdf' | 'text' | 'unsupported';

async function detectKindFromBytes(file?: Blob | ArrayBuffer): Promise<PreviewKind> {
  if (!file) return 'unsupported';
  let buffer: Uint8Array;

  if (file instanceof Blob) {
    buffer = new Uint8Array(await file.slice(0, 5).arrayBuffer());
  } else {
    buffer = new Uint8Array(file).subarray(0, 5);
  }

  // PDF: %PDF-
  if (
    buffer[0] === 0x25 && // %
    buffer[1] === 0x50 && // P
    buffer[2] === 0x44 && // D
    buffer[3] === 0x46 && // F
    buffer[4] === 0x2d // -
  ) {
    return 'pdf';
  }

  return 'unsupported';
}

/**
 * Decide which preview strategy to use based on MIME type.
 * Keep conservative: image/* and application/pdf by default.
 * Optionally allow text/* or CSV if you want inline text preview.
 */
export async function getPreviewKind(
  mime?: string,
  opts?: { allowText?: boolean; allowCsvAsText?: boolean },
  blob?: Blob,
): Promise<PreviewKind> {
  if (!mime) return 'unsupported';
  const m = mime.toLowerCase();

  if (m.startsWith('image/')) return 'image';
  if (m === 'application/pdf') return 'pdf';
  if (m === 'application/octet-stream') {
    return await detectKindFromBytes(blob);
  }

  // Optional text previews (enable via flags)
  const allowText = !!opts?.allowText;
  const allowCsvAsText = !!opts?.allowCsvAsText;
  if (allowText && m.startsWith('text/')) return 'text';
  if (allowCsvAsText && (m === 'text/csv' || m === 'application/csv')) return 'text';

  return 'unsupported';
}

export const isPdfEncrypted = async (file: File): Promise<boolean> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    // Try to load the PDF
    await PDFDocument.load(arrayBuffer, { ignoreEncryption: false });
    // If it loads without error, it's not encrypted
    return false;
  } catch (e: any) {
    // If loading throws, it's likely encrypted
    if (e.message.includes('Password protected') || e.message.includes('encrypted')) {
      return true;
    }
    // Other errors not encrypted for now
    return false;
  }
};
